// localStorage keys for client-side state. The thumbnail cache persists
// generated video stills across page loads so the grid is instant on
// reload. The folders catalog is purely client-side until the backend
// gets a real folders table.
export const THUMB_CACHE_PREFIX  = 'meno_vthumb_'
export const FOLDERS_STORAGE_KEY = 'meno_asset_folders_v1'
// Map of `${kind}:${id}` → folderId. `kind` is one of 'm' (media) | 'a'
// (audio) | 'l' (link). Keys missing from the map mean "top-level" (no
// folder assignment). Lives in localStorage until the backend grows a
// `folder_id` column on each asset table.
export const ASSET_FOLDER_MAP_KEY = 'meno_asset_folder_map_v1'

export const MEDIA_UPLOAD_LIMIT_BYTES = 100 * 1024 * 1024
export const AUDIO_UPLOAD_LIMIT_BYTES = 50 * 1024 * 1024

/** Match the API limits before staging previews or starting an upload. */
export function uploadLimitError(file: File): string | null {
  const audio = file.type.toLowerCase().startsWith('audio/') ||
    /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name)
  const maxBytes = audio ? AUDIO_UPLOAD_LIMIT_BYTES : MEDIA_UPLOAD_LIMIT_BYTES
  if (file.size <= maxBytes) return null
  const maxMb = maxBytes / (1024 * 1024)
  return `“${file.name}” is too large. Maximum upload size is ${maxMb} MB.`
}

// Display labels for the brand-asset slot keys returned by /brand-assets.
// Used as overlay pills on GridCard tiles in the "Brand Assets" view.
export const SLOT_LABELS: Record<string, string> = {
  logo: 'Logo', logo_secondary: 'Logo (Alt)', banner: 'Banner',
  event_banner: 'Event Banner', favicon: 'Favicon',
  watermark: 'Watermark', banner_phone: 'Banner (Phone)', event_banner_phone: 'Event Banner (Phone)',
}

// ── Types ─────────────────────────────────────────────────────────────────

export type MediaFile = {
  id: number
  original_name: string
  file_type: string
  file_size?: number
  url: string
  duration?: number
  created_at: string
  // Soft-delete marker from the backend. NULL/undefined = live in the
  // library; an ISO timestamp = the item lives in the Archive.
  archived_at?: string | null
  // Optional client-side tag: which folder (Folder.id) the asset is filed
  // under. NULL / undefined = top-level (no folder). Stored in localStorage
  // along with the folders catalog since the backend doesn't have a folder
  // column yet; swap to a real server field when /media gets folder_id.
  folder_id?: string | null
}

export type AudioFile = {
  id: number
  original_name: string
  file_type: string
  file_size?: number
  url: string
  duration?: number
  created_at: string
  archived_at?: string | null
  folder_id?: string | null
}

// One track inside an audio playlist — the AudioFile fields plus its per-row
// playback settings. Mirrors the backend AudioPlaylistTrack schema.
export type AudioPlaylistTrack = {
  id: number
  original_name: string
  url: string
  duration?: number
  position: number
  volume: number
  loop: boolean
}

// An audio-only playlist authored + streamed from the Audio Hub. Distinct from
// the visual content Playlist entity.
export type AudioPlaylist = {
  id: number
  name: string
  description?: string | null
  audio_loop_all: boolean
  tracks: AudioPlaylistTrack[]
  track_count: number
  created_at: string
}

// Centralised type guard so every consumer reads audio consistently. We
// accept both the MIME prefix (audio/*) and the extension fallback for
// devices that mis-report MIME on upload.
export const isAudioAsset = (type: string = '', name: string = ''): boolean =>
  type.toLowerCase().startsWith('audio') || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(name)

export type PlaylistScreen = { id: number; name: string }

export type Playlist = {
  id: number
  name: string
  description?: string
  media: MediaFile[]
  media_durations: Record<number, number>
  total_duration: number
  screens: PlaylistScreen[]
  created_at: string
  is_event?: boolean
  event_start_date?: string | null
  event_end_date?: string | null
  event_start_time?: string | null
  event_end_time?: string | null
}

export type EventFields = {
  is_event:         boolean
  event_start_date: string | null
  event_end_date:   string | null
  event_start_time: string | null
  event_end_time:   string | null
}

export type ProductLite = { id: number; name: string; image_url?: string | null }

// Folders now live on the server (see backend folders table + /folders API).
// These loaders remain only to read any legacy per-browser data so MediaPage
// can migrate it up to the server once, after which the localStorage keys are
// cleared. The matching `save*` writers were removed with that migration.
export type Folder = { id: string; name: string; created_at: string; parent_id?: string | null }

export function loadFolders(): Folder[] {
  try {
    const raw = localStorage.getItem(FOLDERS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch { return [] }
}

// ── Asset ↔ Folder mapping (legacy localStorage, migration-read only) ─────
// Keyed map of asset → folder, read once for the server migration described
// above. Membership is authoritative on the server now.

export type AssetKind = 'm' | 'a' | 'l'  // media | audio | link

export function loadAssetFolderMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(ASSET_FOLDER_MAP_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {}
  } catch { return {} }
}

export const assetMapKey = (kind: AssetKind, id: number | string) => `${kind}:${id}`

// Strip protocol+host so relative and absolute URLs for the same file
// compare equal (needed when matching brand-asset rows to media URLs).
export function normalizeMediaUrl(url: string): string {
  try { return new URL(url).pathname } catch { return url }
}

// ── Audio duration probe ──────────────────────────────────────────────────
//
// Reads a file's playback length (in whole seconds) by loading only its
// metadata into a throwaway <audio> element. Used at upload time so the track
// length can be persisted (the backend doesn't decode audio), and resolves to
// 0 on any error/timeout so a bad file never blocks the upload flow.
export function readAudioDuration(source: File | string): Promise<number> {
  return new Promise(resolve => {
    let settled = false
    const done = (d: number) => { if (!settled) { settled = true; resolve(Math.round(d) || 0) } }

    const timer = setTimeout(() => done(0), 15_000)
    const audio = document.createElement('audio')
    audio.preload = 'metadata'
    const isFile = source instanceof File
    const url = isFile ? URL.createObjectURL(source) : source
    const cleanup = () => { clearTimeout(timer); if (isFile) URL.revokeObjectURL(url); audio.remove() }

    audio.addEventListener('loadedmetadata', () => {
      const d = Number.isFinite(audio.duration) ? audio.duration : 0
      cleanup()
      done(d)
    })
    audio.addEventListener('error', () => { cleanup(); done(0) })
    audio.src = url
    audio.load()
  })
}

// ── Video thumbnail capture ───────────────────────────────────────────────
//
// Renders the source frame to a hidden canvas and returns a data: URL.
// Used both at upload time (snapshot before the server has a thumbnail) and
// on the All/Media grids (backfill thumbs for older videos that don't have
// them server-side). 15s timeout so a hung video tag doesn't leak forever.

export function captureVideoThumbnail(source: File | string): Promise<{ preview: string | null; duration: number }> {
  return new Promise(resolve => {
    let settled = false
    const done = (v: string | null, d: number) => { if (!settled) { settled = true; resolve({ preview: v, duration: d }) } }

    const timer = setTimeout(() => done(null, 0), 15_000)

    const video = document.createElement('video')
    video.muted       = true
    video.crossOrigin = 'anonymous'
    video.preload     = 'metadata'
    const isFile = source instanceof File
    const url    = isFile ? URL.createObjectURL(source) : source
    video.src = url
    video.addEventListener('loadeddata', () => {
      video.currentTime = Math.min(1, video.duration > 0 ? video.duration / 4 : 1)
    })
    video.addEventListener('seeked', () => {
      clearTimeout(timer)
      try {
        const canvas = document.createElement('canvas')
        canvas.width  = video.videoWidth  || 320
        canvas.height = video.videoHeight || 180
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
        done(canvas.toDataURL('image/jpeg', 0.7), video.duration || 0)
      } catch {
        done(null, video.duration || 0)
      } finally {
        if (isFile) URL.revokeObjectURL(url)
        video.remove()
      }
    })
    video.addEventListener('error', () => {
      clearTimeout(timer)
      if (isFile) URL.revokeObjectURL(url)
      done(null, 0)
    })
    video.load()
  })
}
